import { AnalyticEvent, defineAnalyticEvents } from 'reactApp/utils/experimentAnalytic';

import { AnalyticEventNames } from './eventNames';
import type {
    BigFileDialogArgs,
    BillingBuyTariffArgs,
    BuyDisableAdsModalShownType,
    BuyDisableAdsModalSwitchPeriodType,
    BuyDisableAdsModalTariffClickType,
    BuyFastCheckoutArgsType,
    BuyModalTrialArgs,
    DisableButtonArgsType,
    DiscountInOutflowModalArgs,
    FastCheckoutShowArgsType,
    FlexibleQuotaChangeArgs,
    FlexibleQuotaShowArgs,
    MailPopupArgs,
    MobileTariffShowEventArgsType,
    OnboardingTooltipSource,
    QuotaLandingArgsType,
    TariffBuyEventArgsType,
    TariffClickEventArgsType,
    TariffShowEventArgsType,
    TariffsShowEventArgs,
    TrialExpiryReminder,
    UnlimitedDocumentsDocTypeArg,
    UnlimitedDocumentsEntryPointArg,
    UnlimitedDocumentsModalArgs,
    VkMusicConfirmArgs,
    VkMusicErrorArgs,
    VkMusicOfferArgs,
    VkMusicSuccessArgs,
    VkMusicSuccessClickArgs,
} from './types';

export const { emitAnalyticEvent, registerExperiment } = defineAnalyticEvents({
    [AnalyticEventNames.QUOTA_LANDING_SHOWN]: new AnalyticEvent<QuotaLandingArgsType>('landing_show'),
    [AnalyticEventNames.MOBILE_LANDING_SHOWN]: new AnalyticEvent<QuotaLandingArgsType>('mob_landing_show'),
    [AnalyticEventNames.TARIFFS_SHOWN]: new AnalyticEvent<TariffsShowEventArgs>('tariffs_show'),
    [AnalyticEventNames.TARIFF_SHOWN]: new AnalyticEvent<TariffShowEventArgsType>('tariff_shown'),
    [AnalyticEventNames.MOBILE_TARIFF_SHOWN]: new AnalyticEvent<MobileTariffShowEventArgsType>('m_tariff_shown'),
    [AnalyticEventNames.TARIFF_ADVANTAGES_CLICK]: new AnalyticEvent('advnt_click'),
    [AnalyticEventNames.MODAL_TARIFF_ADVANTAGES_SHOW]: new AnalyticEvent('advnt_modal_show'),
    [AnalyticEventNames.TARIFF_CLICK]: new AnalyticEvent<TariffClickEventArgsType>('tariff_click'),

    [AnalyticEventNames.FAST_CHECKOUT_SHOWN]: new AnalyticEvent<FastCheckoutShowArgsType>('checkout'),
    [AnalyticEventNames.TARIFF_BUY]: new AnalyticEvent<TariffBuyEventArgsType>('buy'),
    [AnalyticEventNames.TARIFF_BACK_CLICK]: new AnalyticEvent<TariffBuyEventArgsType>('click-back'),
    [AnalyticEventNames.TARIFF_CLOSE]: new AnalyticEvent<TariffBuyEventArgsType>('close'),

    [AnalyticEventNames.TRIAL_TARIFF_BUY]: new AnalyticEvent<BuyModalTrialArgs>('buy-trial'),
    [AnalyticEventNames.TRIAL_TARIFF_SHOW]: new AnalyticEvent<BuyModalTrialArgs>('show-trial'),

    [AnalyticEventNames.BIG_FILE_DIALOG_SHOW]: new AnalyticEvent<BigFileDialogArgs>('big-file-show'),
    [AnalyticEventNames.BIG_FILE_DIALOG_CLOSE]: new AnalyticEvent<BigFileDialogArgs>('big-file-close'),
    [AnalyticEventNames.BIG_FILE_DIALOG_CANCEL]: new AnalyticEvent<BigFileDialogArgs>('big-file-cancel'),
    [AnalyticEventNames.BIG_FILE_DIALOG_CLICK]: new AnalyticEvent('big-file-click'),
    [AnalyticEventNames.BIG_FILE_DIALOG_PAYMENT_SHOW]: new AnalyticEvent('big-file-payment-show'),
    [AnalyticEventNames.BIG_FILE_DIALOG_PAYMENT_BUY]: new AnalyticEvent<{ tariff: 'trial' | 'tariff' }>('big-file-payment-buy'),
    /* tempexp_18869-next-line tempexp_18955-next-line */
    [AnalyticEventNames.BIG_FILE_CLICK_BUY_ONCE]: new AnalyticEvent('big-file-click-buy-once'),

    [AnalyticEventNames.MAIL_SPACE_TRIAL_PROMO_SHOW]: new AnalyticEvent('promo_show'),
    [AnalyticEventNames.MAIL_SPACE_TRIAL_PROMO_CLICK]: new AnalyticEvent('promo_click'),
    [AnalyticEventNames.MAIL_SPACE_TRIAL_PROMO_CLOSE]: new AnalyticEvent('promo_close'),

    [AnalyticEventNames.MAIL_POPUP_SHOW]: new AnalyticEvent<MailPopupArgs>('mail-popup-show'),
    [AnalyticEventNames.MAIL_POPUP_CLICK]: new AnalyticEvent<MailPopupArgs>('mail-popup-click'),
    [AnalyticEventNames.MAIL_POPUP_PAYMENT_SHOW]: new AnalyticEvent<MailPopupArgs>('mail-popup-payment-show'),
    [AnalyticEventNames.MAIL_POPUP_PAYMENT_SUCCESS]: new AnalyticEvent<MailPopupArgs>('mail-popup-payment-buy'),
    [AnalyticEventNames.MAIL_POPUP_PAYMENT_FAILURE]: new AnalyticEvent<MailPopupArgs>('mail-popup-payment-fail'),

    [AnalyticEventNames.VIEWER_CLOSE]: new AnalyticEvent('viewer-close'),

    [AnalyticEventNames.DISABLE_BUTTON_SHOW]: new AnalyticEvent<DisableButtonArgsType>('disable_show'),
    [AnalyticEventNames.DISABLE_BUTTON_CLICK]: new AnalyticEvent<DisableButtonArgsType>('disable_click'),
    [AnalyticEventNames.DISABLE_BUTTON_SHOW_CHECKOUT]: new AnalyticEvent<DisableButtonArgsType>('disable_checkout'),
    [AnalyticEventNames.DISABLE_BUTTON_PAYMENT]: new AnalyticEvent<DisableButtonArgsType>('disable_payment'),

    [AnalyticEventNames.SIDEBAR_TRIAL_PROMO_SHOW]: new AnalyticEvent('promo-show'),
    [AnalyticEventNames.SIDEBAR_TRIAL_PROMO_CLICK]: new AnalyticEvent('promo-click'),
    [AnalyticEventNames.SIDEBAR_TRIAL_PROMO_CLOSE]: new AnalyticEvent('promo-close'),
    [AnalyticEventNames.SIDEBAR_TRIAL_BANNER_SHOW]: new AnalyticEvent('banner-show'),
    [AnalyticEventNames.SIDEBAR_TRIAL_BANNER_CLICK]: new AnalyticEvent('banner-click'),

    [AnalyticEventNames.QUOTA_TOOLTIP_SHOW]: new AnalyticEvent('quota-tooltip_show'),
    [AnalyticEventNames.QUOTA_TOOLTIP_CLOSE]: new AnalyticEvent('quota-tooltip_close'),
    [AnalyticEventNames.QUOTA_BUTTON_SHOW]: new AnalyticEvent('quota-button_show'),
    [AnalyticEventNames.QUOTA_BUTTON_CLICK]: new AnalyticEvent('quota-button_click'),

    [AnalyticEventNames.BUY_FAST_CHECKOUT_VIEW]: new AnalyticEvent<BuyFastCheckoutArgsType>('buy-fastcheckout-view'),
    [AnalyticEventNames.BUY_FAST_CHECKOUT_CLICK_PAY]: new AnalyticEvent<BuyFastCheckoutArgsType>('buy-fastcheckout-click-pay'),
    [AnalyticEventNames.BUY_FAST_CHECKOUT_CLOSE]: new AnalyticEvent<BuyFastCheckoutArgsType>('buy-fastcheckout-close'),
    [AnalyticEventNames.BUY_FAST_CHECKOUT_BACK]: new AnalyticEvent<BuyFastCheckoutArgsType>('buy-fastcheckout-back'),

    // tempexp_17127-start
    [AnalyticEventNames.BUY_DISABLE_ADS_MODAL_SHOWN]: new AnalyticEvent<BuyDisableAdsModalShownType>('buy_disable_ads_modal_shown'),
    [AnalyticEventNames.BUY_DISABLE_ADS_MODAL_PERIOD_CHANGE]: new AnalyticEvent<BuyDisableAdsModalSwitchPeriodType>(
        'buy_disable_ads_modal_period_change'
    ),
    [AnalyticEventNames.BUY_DISABLE_ADS_MODAL_TARIFF_CLICK]: new AnalyticEvent<BuyDisableAdsModalTariffClickType>(
        'buy_disable_ads_modal_tariff_click'
    ),
    // tempexp_17127-end

    [AnalyticEventNames.DATALIST_ITEM_CLICK_PUBLISH]: new AnalyticEvent('datalist_item_click_publish'),
    [AnalyticEventNames.NICE_LINK_TOOLTIP_SHOW]: new AnalyticEvent('nice-link-tooltip_show'),
    [AnalyticEventNames.NICE_LINK_TOOLTIP_CLICK]: new AnalyticEvent('nice-link-tooltip_click'),
    [AnalyticEventNames.NICE_LINK_TOOLTIP_CLOSE]: new AnalyticEvent('nice-link-tooltip_close'),
    [AnalyticEventNames.NICE_LINK_THANKS_TOOLTIP_CLICK]: new AnalyticEvent('nice-link-thanks-tooltip_click'),
    [AnalyticEventNames.NICE_LINK_THANKS_TOOLTIP_SHOW]: new AnalyticEvent('nice-link-thanks-tooltip_show'),
    // tempexp_17086-start
    [AnalyticEventNames.WELCOME_DEFAULT_TARIFFS_SHOW]: new AnalyticEvent('welcome_show'),
    [AnalyticEventNames.WELCOME_DEFAULT_TARIFFS_CLICK_FREE]: new AnalyticEvent('click_free'),
    // tempexp_17086-end

    [AnalyticEventNames.OVERQUOTA_POPUP_SHOW]: new AnalyticEvent('overquota-show'),
    [AnalyticEventNames.OVERQUOTA_POPUP_CLICK]: new AnalyticEvent('overquota-click'),
    [AnalyticEventNames.QUOTA_BLOCK_OVER_VIEW]: new AnalyticEvent('quota-over-view'),
    [AnalyticEventNames.QUOTA_BLOCK_OVER_CLICK]: new AnalyticEvent('quota-over-click'),
    // tempexp_17125-start
    [AnalyticEventNames.BLOCK_OVERQUOTA_VIEW]: new AnalyticEvent('block-over-view'),
    [AnalyticEventNames.BLOCK_OVERQUOTA_CLICK]: new AnalyticEvent('block-over-click'),
    [AnalyticEventNames.BLOCK_OVERQUOTA_FILES_VIEW]: new AnalyticEvent('block-file-view'),
    [AnalyticEventNames.BLOCK_OVERQUOTA_FILES_CLICK]: new AnalyticEvent('block-file-click'),
    // tempexp_17125-end

    // tempexp_17324-start
    [AnalyticEventNames.MODAL_CHOOSE_TARIFF_SHOW]: new AnalyticEvent('modal-choose-show'),
    [AnalyticEventNames.MODAL_CHOOSE_TARIFF_CONTINUE]: new AnalyticEvent('modal-choose-continue'),
    // tempexp_17324-end

    // tempexp_17509-start
    [AnalyticEventNames.LITRES_PROMO_SHOW]: new AnalyticEvent('litres-promo-show'),
    [AnalyticEventNames.LITRES_PROMO_TOOLTIP_SHOW]: new AnalyticEvent('litres-promo-tooltip-show'),
    [AnalyticEventNames.LITRES_PROMO_TOOLTIP_CLICK]: new AnalyticEvent('litres-promo-tooltip-click'),
    [AnalyticEventNames.LITRES_PROMO_TOOLTIP_CLOSE]: new AnalyticEvent('litres-promo-tooltip-close'),
    [AnalyticEventNames.LITRES_PROMO_SUCCESS_SHOW]: new AnalyticEvent('litres-promo-success-show'),
    [AnalyticEventNames.LITRES_PROMO_SUCCESS_CLICK_CHECK]: new AnalyticEvent('litres-promo-success-check'),
    [AnalyticEventNames.LITRES_PROMO_SUCCESS_CLICK_LATER]: new AnalyticEvent('litres-promo-success-later'),
    [AnalyticEventNames.LITRES_PROMO_SUCCESS_CLOSE]: new AnalyticEvent('litres-promo-success-close'),
    // tempexp_17509-end

    // tempexp_17532-start
    [AnalyticEventNames.LITRES_MODAL_CLOUD_SHOW]: new AnalyticEvent('litres-modal-cloud-show'),
    [AnalyticEventNames.LITRES_MODAL_CLOUD_CLICK]: new AnalyticEvent('litres-modal-cloud-click'),
    [AnalyticEventNames.LITRES_MODAL_CLOUD_CANCEL]: new AnalyticEvent('litres-modal-cloud-cancel'),
    [AnalyticEventNames.LITRES_MODAL_CLOUD_CLOSE]: new AnalyticEvent('litres-modal-cloud-close'),
    // tempexp_17532-end

    [AnalyticEventNames.POPUP_BANNER_OFFER_SHOW]: new AnalyticEvent('popup-offer-show'),
    [AnalyticEventNames.POPUP_BANNER_OFFER_CLICK]: new AnalyticEvent('popup-offer-click'),
    [AnalyticEventNames.POPUP_BANNER_OFFER_CLOSE]: new AnalyticEvent('popup-offer-close'),
    [AnalyticEventNames.POPUP_BANNER_OFFER_BUY]: new AnalyticEvent('popup-offer-buy'),

    // tempexp_17324-start
    [AnalyticEventNames.TOOLTIP_BRAND_PAGE_SHOW]: new AnalyticEvent('tooltip-brand-page-show'),
    [AnalyticEventNames.TOOLTIP_BRAND_PAGE_CLICK]: new AnalyticEvent('tooltip-brand-page-click'),
    [AnalyticEventNames.TOOLTIP_BRAND_PAGE_CLOSE]: new AnalyticEvent('tooltip-brand-page-close'),
    [AnalyticEventNames.BREADCRUMBS_SHOW]: new AnalyticEvent('breadcrumbs-show'),
    [AnalyticEventNames.TOOLBAR_ITEM_SHOW_BRAND_PAGE]: new AnalyticEvent('toolbar-item-show-brand-page'),
    [AnalyticEventNames.TOOLBAR_ITEM_CLICK_BRAND_PAGE]: new AnalyticEvent('toolbar-item-click-brand-page'),
    [AnalyticEventNames.BREADCRUMBS_PUBLISH_CLICK]: new AnalyticEvent('breadcrumbs-publish-click'),
    [AnalyticEventNames.SHARING_BRAND_PAGE_SHOW]: new AnalyticEvent('sharing-brand-page-show'),
    [AnalyticEventNames.SHARING_BRAND_PAGE_CLICK]: new AnalyticEvent('sharing-brand-page-click'),
    [AnalyticEventNames.SHARING_TOOLTIP_BRAND_PAGE_SHOW]: new AnalyticEvent('sharing-tooltip-brand-page-show'),
    [AnalyticEventNames.SHARING_TOOLTIP_BRAND_PAGE_CLOSE]: new AnalyticEvent('sharing-tooltip-brand-page-close'),
    [AnalyticEventNames.TOOLBAR_BRAND_PAGE_SHOW]: new AnalyticEvent('toolbar-brand-page-show'),
    [AnalyticEventNames.TOOLBAR_BRAND_PAGE_CLICK]: new AnalyticEvent('toolbar-brand-page-click'),
    [AnalyticEventNames.BRAND_PAGE_VIEW]: new AnalyticEvent('brand-page-view'),
    [AnalyticEventNames.BRAND_PAGE_SAVE]: new AnalyticEvent('brand-page-save'),
    [AnalyticEventNames.BRAND_PAGE_TARIFF_PLAN_CHOOSE]: new AnalyticEvent('brand-page-tariff-plan-choose'),
    [AnalyticEventNames.BRAND_PAGE_TARIFF_CLICK]: new AnalyticEvent('brand-page-tariff-click'),
    [AnalyticEventNames.BRAND_PAGE_HIDE]: new AnalyticEvent('brand-page-hide'),
    [AnalyticEventNames.BRAND_PAGE_EXIT_SHOW]: new AnalyticEvent('brand-page-exit-shown'),
    [AnalyticEventNames.BRAND_PAGE_EXIT_CHOOSE]: new AnalyticEvent('brand-page-exit-choose'),
    [AnalyticEventNames.BRAND_PAGE_EXIT_TRUE]: new AnalyticEvent('brand-page-exit-true'),
    [AnalyticEventNames.BRAND_PUBLIC_PROMO_SHOW]: new AnalyticEvent('brand-promo-show'),
    [AnalyticEventNames.BRAND_PUBLIC_PROMO_CLICK]: new AnalyticEvent('brand-promo-click'),
    [AnalyticEventNames.BRAND_PUBLIC_PROMO_CLOSE]: new AnalyticEvent('brand-promo-close'),
    // tempexp_17324-end

    [AnalyticEventNames.WELCOME_TRIAL_BANNER_SHOW]: new AnalyticEvent('banner-show'),
    [AnalyticEventNames.WELCOME_TRIAL_BANNER_CLICK]: new AnalyticEvent('banner-click'),
    [AnalyticEventNames.WELCOME_TRIAL_BANNER_BUY]: new AnalyticEvent('banner-buy'),

    [AnalyticEventNames.PUBLIC_PASSWORD_SHOW]: new AnalyticEvent('public-pswd-show'),
    [AnalyticEventNames.PUBLIC_PASSWORD_CLICK]: new AnalyticEvent('public-pswd-click'),
    [AnalyticEventNames.PUBLIC_PASSWORD_SET]: new AnalyticEvent('public-pswd-set'),
    [AnalyticEventNames.PUBLIC_PASSWORD_SAVE]: new AnalyticEvent('public-pswd-save'),
    [AnalyticEventNames.PUBLIC_PASSWORD_PROMO_SHOW]: new AnalyticEvent('public-pswd-promo-show'),
    [AnalyticEventNames.PUBLIC_PASSWORD_PROMO_CLICK]: new AnalyticEvent('public-pswd-promo-click'),
    [AnalyticEventNames.PUBLIC_PASSWORD_GUEST_SHOW]: new AnalyticEvent('public-pswd-guest-show'),
    [AnalyticEventNames.PUBLIC_PASSWORD_GUEST_INCORRECT]: new AnalyticEvent('public-pswd-guest-error'),
    [AnalyticEventNames.PUBLIC_PASSWORD_GUEST_SUCCESS]: new AnalyticEvent('public-pswd-guest-success'),

    // tempexp_17825-start
    [AnalyticEventNames.CARD_EDIT_SIDEBAR]: new AnalyticEvent('card-edit-sidebar'),
    [AnalyticEventNames.CARD_REMOVE_SIDEBAR]: new AnalyticEvent('card-remove-sidebar'),
    [AnalyticEventNames.CARD_REMOVE_CONFIRM]: new AnalyticEvent('card-remove-confirm'),
    // tempexp_17825-end

    [AnalyticEventNames.MY_TARIFF_CANCEL_SALE_SHOW]: new AnalyticEvent('my-tariff-cancel-sale-show'),
    [AnalyticEventNames.MY_TARIFF_CANCEL_SALE_GET]: new AnalyticEvent('my-tariff-cancel-sale-get'),
    [AnalyticEventNames.MY_TARIFF_CANCEL_SALE_REJECT]: new AnalyticEvent('my-tariff-cancel-sale-reject'),
    [AnalyticEventNames.MY_TARIFF_CANCEL_SALE_CLOSE]: new AnalyticEvent('my-tariff-cancel-sale-close'),

    [AnalyticEventNames.STATUS_202_ON_BUY_SUBSCRIPTION]: new AnalyticEvent('buy-subs-202'),
    [AnalyticEventNames.REPEAT_PURCHASE_MOBILE_SHOW]: new AnalyticEvent('repeat-purchase-show'),
    [AnalyticEventNames.REPEAT_PURCHASE_MOBILE_CANCEL]: new AnalyticEvent('repeat-purchase-cancel'),
    [AnalyticEventNames.REPEAT_PURCHASE_MOBILE_CLICK]: new AnalyticEvent('repeat-purchase-click'),
    [AnalyticEventNames.REPEAT_PURCHASE_MOBILE_CLOSE]: new AnalyticEvent('repeat-purchase-close'),

    // tempexp_17360-start
    [AnalyticEventNames.UPSALE_BONUS_SHOW]: new AnalyticEvent('upsale-bonus-show'),
    // tempexp_17360-end

    // tempexp_17990-start
    [AnalyticEventNames.QUOTA_BLOCK_SPACE_BUTTON_CLICK]: new AnalyticEvent('quota-space-btn-click'),
    [AnalyticEventNames.QUOTA_BLOCK_SPACE_BUTTON_VIEW]: new AnalyticEvent('quota-space-btn-view'),
    [AnalyticEventNames.QUOTA_BLOCK_MENU_CLICK]: new AnalyticEvent('quota-menu-btn-click'),
    // tempexp_17990-end

    // tempexp_18090-start
    [AnalyticEventNames.TARIFF_POLL_SHOW]: new AnalyticEvent('tariff-poll-show'),
    [AnalyticEventNames.TARIFF_POLL_SUCCESS_SHOW]: new AnalyticEvent('tariff-poll-success-show'),
    // tempexp_18090-end

    // tempexp_18089-start
    [AnalyticEventNames.PARTNERS_FAKEDOOR_VIEW]: new AnalyticEvent('partners-fakedoor-view'),
    [AnalyticEventNames.PARTNERS_FAKEDOOR_MODAL_VIEW]: new AnalyticEvent('partners-fakedoor-modal'),
    // tempexp_18089-end

    [AnalyticEventNames.CANCEL_SUB_TARIFF]: new AnalyticEvent('cancel-sub-tariff'),
    [AnalyticEventNames.CANCEL_SUB_CAPTCHA]: new AnalyticEvent('cancel-sub-captcha'),
    [AnalyticEventNames.CANCEL_SUB_CAPTCHA_CANCEL]: new AnalyticEvent('cancel-sub-captcha-cancel'),
    [AnalyticEventNames.CANCEL_SUB_SUCCESS]: new AnalyticEvent('cancel-sub-success'),

    // tempexp_17359-start
    [AnalyticEventNames.CANCEL_SUB_EMPTY_FAMILY]: new AnalyticEvent('cancel-sub-fam-empty'),
    [AnalyticEventNames.CANCEL_SUB_EMPTY_FAMILY_CLICK]: new AnalyticEvent('cancel-sub-fam-empty-click'),
    [AnalyticEventNames.CANCEL_SUB_FAMILY]: new AnalyticEvent('cancel-sub-fam'),
    [AnalyticEventNames.CANCEL_SUB_FAMILY_CLICK]: new AnalyticEvent('cancel-sub-fam-click'),
    [AnalyticEventNames.CANCEL_SUB_FAMILY_CANCEL]: new AnalyticEvent('cancel-sub-fam-cancel'),
    // tempexp_17359-end

    // tempexp_18238-start
    [AnalyticEventNames.FAMILY_AFTER_PURCHASE_MODAL_SHOW]: new AnalyticEvent('family-after-purchase-show'),
    [AnalyticEventNames.FAMILY_AFTER_PURCHASE_MODAL_CLICK]: new AnalyticEvent('family-after-purchase-click'),
    [AnalyticEventNames.FAMILY_AFTER_PURCHASE_MODAL_CANCEL]: new AnalyticEvent('family-after-purchase-cancel'),
    [AnalyticEventNames.FAMILY_AFTER_PURCHASE_MODAL_CLOSE]: new AnalyticEvent('family-after-purchase-close'),
    [AnalyticEventNames.FAMILY_INVITE_LINK_SHOW]: new AnalyticEvent('family-invite-link-show'),
    [AnalyticEventNames.FAMILY_INVITE_LINK_CLICK]: new AnalyticEvent('family-invite-link-click'),
    [AnalyticEventNames.FAMILY_INVITE_LINK_CLOSE]: new AnalyticEvent('family-invite-link-close'),
    // tempexp_18238-end

    [AnalyticEventNames.BEFORE_PURCHASE_MESSAGE_STATUS]: new AnalyticEvent('before-purchase-status'),
    [AnalyticEventNames.BEFORE_PURCHASE_MESSAGE_SHOW]: new AnalyticEvent('before-purchase-show'),
    [AnalyticEventNames.BEFORE_PURCHASE_MESSAGE_CLICK]: new AnalyticEvent('before-purchase-click'),
    [AnalyticEventNames.BEFORE_PURCHASE_MESSAGE_CLOSE]: new AnalyticEvent('before-purchase-close'),

    [AnalyticEventNames.ENABLE_SHARING_SHOW]: new AnalyticEvent('enable-sharing-show'),
    [AnalyticEventNames.SHARING_ADVANCED_SETTINGS_PUBLIC_HIDDEN]: new AnalyticEvent('sharing-adv-stngs-hidden'),
    [AnalyticEventNames.SHARING_ADVANCED_SETTINGS_PUBLIC_SHOW]: new AnalyticEvent('sharing-adv-stngs-show'),
    [AnalyticEventNames.ONBOARDING_TRIAL_BUTTON_CLICK]: new AnalyticEvent('onboarding-trial-btn-click'),
    [AnalyticEventNames.ONBOARDING_TRIAL_BUTTON_SHOW]: new AnalyticEvent('onboarding-trial-btn-show'),
    [AnalyticEventNames.ONBOARDING_TOOLTIP_SHOW]: new AnalyticEvent<OnboardingTooltipSource>('onboarding-tooltip-show'),
    [AnalyticEventNames.ONBOARDING_TOOLTIP_CLOSE]: new AnalyticEvent<OnboardingTooltipSource>('onboarding-tooltip-close'),
    [AnalyticEventNames.ONBOARDING_AUTODELETE_SHOW]: new AnalyticEvent('onboarding-autodelete-show'),
    [AnalyticEventNames.ONBOARDING_AUTODELETE_CLICK]: new AnalyticEvent('onboarding-autodelete-click'),

    [AnalyticEventNames.EDITOR_VIEWER_OPEN]: new AnalyticEvent('editor-viewer-open'),
    [AnalyticEventNames.EDITOR_BUTTON_SHOW]: new AnalyticEvent('editor-button-show'),
    [AnalyticEventNames.EDITOR_BUTTON_CLICK]: new AnalyticEvent('editor-button-click'),
    [AnalyticEventNames.EDITOR_TOOLTIP_SHOW_512]: new AnalyticEvent('editor-tooltip-512-show'),
    [AnalyticEventNames.EDITOR_TOOLTIP_CLICK_512]: new AnalyticEvent('editor-tooltip-512-click'),
    [AnalyticEventNames.EDITOR_TOOLTIP_CLICK_TARIFF_512]: new AnalyticEvent('editor-tooltip-512-click-tariff'),

    [AnalyticEventNames.EDITOR_VIEWER_OPEN_CONTENT]: new AnalyticEvent('editor-viewer-open-content'),
    [AnalyticEventNames.EDITOR_BALLOON_SHOW_512]: new AnalyticEvent('editor-balloon-512-show'),
    [AnalyticEventNames.EDITOR_BALLOON_CLICK_512]: new AnalyticEvent('editor-balloon-512-click'),
    [AnalyticEventNames.EDITOR_BALLOON_CLICK_TARIFF_512]: new AnalyticEvent('editor-balloon-512-click-tariff'),
    [AnalyticEventNames.EDITOR_BALLOON_CLOSE_512]: new AnalyticEvent('editor-balloon-512-close'),

    [AnalyticEventNames.EDITOR_SPLASHSCREEN_ALERT_SHOW_512]: new AnalyticEvent('editor-splashscreen-alert-512-show'),
    [AnalyticEventNames.EDITOR_SPLASHSCREEN_ALERT_CLICK_512]: new AnalyticEvent('editor-splashscreen-alert-512-click'),
    [AnalyticEventNames.EDITOR_SPLASHSCREEN_ALERT_CLICK_TARIFF_512]: new AnalyticEvent('editor-splashscreen-alert-512-click-tariff'),
    [AnalyticEventNames.EDITOR_SPLASHSCREEN_ALERT_CLOSE_512]: new AnalyticEvent('editor-splashscreen-alert-512-close'),

    // tempexp_18683-start
    [AnalyticEventNames.FLEXIBLE_QUOTA_PROMO_SHOW]: new AnalyticEvent('flexible-quota-bubble-show'),
    [AnalyticEventNames.FLEXIBLE_QUOTA_PROMO_CLICK]: new AnalyticEvent('flexible-quota-bubble-click'),
    [AnalyticEventNames.FLEXIBLE_QUOTA_PROMO_CLOSE]: new AnalyticEvent('flexible-quota-bubble-сlose'),
    [AnalyticEventNames.FLEXIBLE_QUOTA_BUTTON_SHOW]: new AnalyticEvent('flexible-quota-button-show'),
    [AnalyticEventNames.FLEXIBLE_QUOTA_BUTTON_CLICK]: new AnalyticEvent('flexible-quota-button-click'),
    [AnalyticEventNames.FLEXIBLE_QUOTA_PROMO_LAND_SHOW]: new AnalyticEvent('flexible-quota-block-show'),
    [AnalyticEventNames.FLEXIBLE_QUOTA_PROMO_LAND_CLICK]: new AnalyticEvent('flexible-quota-block-click'),
    [AnalyticEventNames.FLEXIBLE_QUOTA_MODAL_SHOW]: new AnalyticEvent<FlexibleQuotaShowArgs>('flexible-quota-modal-show'),
    [AnalyticEventNames.FLEXIBLE_QUOTA_MODAL_CHANGE]: new AnalyticEvent<FlexibleQuotaChangeArgs>('flexible-quota-modal-change'),
    [AnalyticEventNames.FLEXIBLE_QUOTA_MODAL_CLICK]: new AnalyticEvent<FlexibleQuotaShowArgs>('flexible-quota-modal-click'),
    // tempexp_18683-end

    [AnalyticEventNames.ASIDE_POLL_SHOW]: new AnalyticEvent('aside-poll-show'),
    [AnalyticEventNames.ASIDE_POLL_START]: new AnalyticEvent('aside-poll-start'),
    [AnalyticEventNames.ASIDE_POLL_RESULTS_SHOW]: new AnalyticEvent('aside-poll-result'),
    [AnalyticEventNames.ASIDE_POLL_RESULTS_CLICK]: new AnalyticEvent('aside-poll-result-click'),

    // tempexp_18696-start
    [AnalyticEventNames.ASIDE_COVERAGE_CAMPAIGN_SHOW]: new AnalyticEvent('aside-cvg-show'),
    [AnalyticEventNames.ASIDE_COVERAGE_CAMPAIGN_CLICK]: new AnalyticEvent('aside-cvg-click'),
    // tempexp_18696-end

    [AnalyticEventNames.PAUSE_SUBS_CANCEL]: new AnalyticEvent('pause-subs-cancel'),
    [AnalyticEventNames.PAUSE_SUBS_CLICK]: new AnalyticEvent('pause-subs-click'),
    [AnalyticEventNames.PAUSE_SUBS_SHOW]: new AnalyticEvent('pause-subs-show'),

    [AnalyticEventNames.MY_TARIFF_OPEN]: new AnalyticEvent<TrialExpiryReminder>('my-tariff-open'),
    [AnalyticEventNames.MY_TARIFF_CANCEL_AUTOPAY]: new AnalyticEvent<TrialExpiryReminder>('my-tariff-cancel-autopay'),
    [AnalyticEventNames.MY_TARIFF_CANCEL_AUTOPAY_SHOW]: new AnalyticEvent<TrialExpiryReminder>('my-tariff-cancel-autopay-show'),
    [AnalyticEventNames.MY_TARIFF_CANCEL_AUTOPAY_SAVE]: new AnalyticEvent<TrialExpiryReminder>('my-tariff-cancel-autopay-save'),
    [AnalyticEventNames.MY_TARIFF_PROLONG_SUB]: new AnalyticEvent<TrialExpiryReminder>('my-tariff-prolong-sub'),
    [AnalyticEventNames.MY_TARIFF_CANCEL_AUTOPAY_TRUE]: new AnalyticEvent<TrialExpiryReminder>('my-tariff-cancel-autopay-true'),

    [AnalyticEventNames.POLL_OPEN]: new AnalyticEvent('poll-open'),
    [AnalyticEventNames.POLL_SEND]: new AnalyticEvent('poll-send'),

    [AnalyticEventNames.VK_MUSIC_TARIFF_CANCEL_VIEW]: new AnalyticEvent('vk-music-tariff-cancel-view'),
    [AnalyticEventNames.VK_MUSIC_TARIFF_CANCEL_CANCEL]: new AnalyticEvent('vk-music-tariff-cancel-cancel'),
    [AnalyticEventNames.VK_MUSIC_TARIFF_CANCEL_SAVE]: new AnalyticEvent('vk-music-tariff-cancel-save'),

    // tempexp_18851-start
    [AnalyticEventNames.PAID_USER_RETURN_MODAL_SHOW]: new AnalyticEvent('paid-users-modal-show'),
    [AnalyticEventNames.PAID_USER_RETURN_MODAL_CLICK]: new AnalyticEvent('paid-users-modal-click'),
    [AnalyticEventNames.PAID_USER_RETURN_MODAL_CLOSE]: new AnalyticEvent('paid-users-modal-close'),
    // tempexp_18851-end

    [AnalyticEventNames.EXTEND_QUOTA_OFFER_SHOW]: new AnalyticEvent('extend-quota-offer-show'),
    [AnalyticEventNames.EXTEND_QUOTA_OFFER_PRIMARY_CLICK]: new AnalyticEvent('extend-quota-offer-primary-click'),
    [AnalyticEventNames.EXTEND_QUOTA_OFFER_SECONDARY_CLICK]: new AnalyticEvent('extend-quota-offer-secondary-click'),
    [AnalyticEventNames.EXTEND_QUOTA_OFFER_CLOSE]: new AnalyticEvent('extend-quota-offer-close'),

    // tempexp_18855-start
    [AnalyticEventNames.CROSS_PROMO_VK_MUSIC_FAKEDOOR_SHOW]: new AnalyticEvent('vk-music-modal-show'),
    [AnalyticEventNames.CROSS_PROMO_VK_MUSIC_FAKEDOOR_CLICK]: new AnalyticEvent('vk-music-modal-click'),
    [AnalyticEventNames.CROSS_PROMO_VK_MUSIC_SEVERAL_SUBSCRIPTIONS_SHOW]: new AnalyticEvent('vk-music-modal-subs-show'),
    [AnalyticEventNames.CROSS_PROMO_VK_MUSIC_SEVERAL_SUBSCRIPTIONS_CLICK]: new AnalyticEvent('vk-music-modal-subs-click'),
    // tempexp_18855-end

    // tempexp_18662-start
    [AnalyticEventNames.PUBLIC_INFO_MODAL_SHOW]: new AnalyticEvent('public-info-modal-show'),
    [AnalyticEventNames.PUBLIC_INFO_DOWNLOADS]: new AnalyticEvent('public-info-modal-downloads'),
    [AnalyticEventNames.PUBLIC_INFO_VIEWS]: new AnalyticEvent('public-info-modal-views'),
    // tempexp_18662-end

    // tempexp_18727-start
    [AnalyticEventNames.SKIPPED_PAYMENT_MODAL_7D_SHOW]: new AnalyticEvent('skip-pay-trial-7d'),
    [AnalyticEventNames.SKIPPED_PAYMENT_MODAL_7D_CLICK]: new AnalyticEvent('skip-pay-trial-7d-click'),
    [AnalyticEventNames.SKIPPED_PAYMENT_MODAL_14D_SHOW]: new AnalyticEvent('skip-pay-trial-14d'),
    [AnalyticEventNames.SKIPPED_PAYMENT_MODAL_14D_CLICK]: new AnalyticEvent('skip-pay-trial-14d-click'),
    // tempexp_18727-end

    // tempexp_17847-start
    [AnalyticEventNames.PUBLIC_LIMIT_PROMO_SHARING_SHOW]: new AnalyticEvent('public-limit-promo-sharing-show'),
    [AnalyticEventNames.PUBLIC_LIMIT_PROMO_SHARING_CLOSE]: new AnalyticEvent('public-limit-promo-sharing-close'),
    [AnalyticEventNames.PUBLIC_LIMIT_PROMO_SHARING_CLICK]: new AnalyticEvent('public-limit-promo-sharing-click'),
    [AnalyticEventNames.PUBLIC_LIMIT_PROMO_LIST_SHOW]: new AnalyticEvent('public-limit-promo-list-show'),
    [AnalyticEventNames.PUBLIC_LIMIT_PROMO_LIST_CLOSE]: new AnalyticEvent('public-limit-promo-list-close'),
    [AnalyticEventNames.PUBLIC_LIMIT_PROMO_LIST_CLICK]: new AnalyticEvent('public-limit-promo-list-click'),
    [AnalyticEventNames.PUBLIC_LIMIT_PROMO_LINK_SHOW]: new AnalyticEvent('public-limit-promo-link-show'),
    [AnalyticEventNames.PUBLIC_LIMIT_PROMO_LINK_CLOSE]: new AnalyticEvent('public-limit-promo-link-close'),
    [AnalyticEventNames.PUBLIC_LIMIT_PROMO_LINK_CLICK]: new AnalyticEvent('public-limit-promo-link-click'),
    [AnalyticEventNames.INFORMER_SHARING_SHOW]: new AnalyticEvent('informer-sharing-show'),
    [AnalyticEventNames.INFORMER_SHARING_CLICK]: new AnalyticEvent('informer-sharing-click'),
    [AnalyticEventNames.INFORMER_HOME_SHOW]: new AnalyticEvent('informer-home-show'),
    [AnalyticEventNames.INFORMER_HOME_CLICK]: new AnalyticEvent('informer-home-click'),
    [AnalyticEventNames.INFORMER_HOME_CLOSE]: new AnalyticEvent('informer-home-close'),
    // tempexp_17847-end

    // tempexp_18907-start
    [AnalyticEventNames.DISCOUNT_LS_SAVE]: new AnalyticEvent('discount_ls_save'),
    [AnalyticEventNames.DISCOUNT_LS_CLEAR]: new AnalyticEvent('discount_ls_clear'),

    [AnalyticEventNames.DISCOUNT_MODAL_SHOW]: new AnalyticEvent<DiscountInOutflowModalArgs>('DISCOUN_MODAL_show'),
    [AnalyticEventNames.DISCOUNT_MODAL_CLICK]: new AnalyticEvent<DiscountInOutflowModalArgs>('DISCOUN_MODAL_enable_click'),
    [AnalyticEventNames.DISCOUNT_MODAL_CANCEL]: new AnalyticEvent<DiscountInOutflowModalArgs>('DISCOUN_MODAL_cancel'),
    [AnalyticEventNames.DISCOUNT_MODAL_ENABLE]: new AnalyticEvent<DiscountInOutflowModalArgs>('DISCOUN_MODAL_enable'),

    [AnalyticEventNames.DISCOUNT_TIMER_SHOW]: new AnalyticEvent('discount_timer_show'),
    [AnalyticEventNames.DISCOUNT_TIMER_CLICK]: new AnalyticEvent('discount_timer_click'),
    [AnalyticEventNames.DISCOUNT_TIMER_CANCEL]: new AnalyticEvent('discount_timer_cancel'),
    // tempexp_18907-end
    // tempexp_18086-start
    [AnalyticEventNames.REFERRAL_BALLON_SHOW]: new AnalyticEvent('referral-ballon-show'),
    [AnalyticEventNames.REFERRAL_BALLON_CLICK]: new AnalyticEvent('referral-ballon-click'),
    [AnalyticEventNames.REFERRAL_BALLON_CLOSE]: new AnalyticEvent('referral-ballon-close'),
    [AnalyticEventNames.REFERRAL_TOOLTIP_SHOW]: new AnalyticEvent('referral-tooltip-show'),
    [AnalyticEventNames.REFERRAL_TOOLTIP_CLICK]: new AnalyticEvent('referral-tooltip-click'),
    [AnalyticEventNames.REFERRAL_TOOLTIP_CLOSE]: new AnalyticEvent('referral-tooltip-close'),
    [AnalyticEventNames.REFERRAL_BANNER_SHOW]: new AnalyticEvent('referral-banner-show'),
    [AnalyticEventNames.REFERRAL_BANNER_CLICK]: new AnalyticEvent('referral-banner-click'),
    [AnalyticEventNames.REFERRAL_BONUS_SPACE_CLICK]: new AnalyticEvent('referral-bonus-space-click'),
    [AnalyticEventNames.REFERRAL_SUBS_BANNER_SHOW]: new AnalyticEvent('referral-subs-banner-show'),
    [AnalyticEventNames.REFERRAL_SUBS_BANNER_CLICK]: new AnalyticEvent('referral-subs-banner-click'),
    [AnalyticEventNames.REFERRAL_BONUS_MODAL_SHOW]: new AnalyticEvent('referral-bonus-modal-show'),
    [AnalyticEventNames.REFERRAL_BONUS_MODAL_CLICK]: new AnalyticEvent('referral-bonus-modal-click'),
    [AnalyticEventNames.REFERRAL_BONUS_MODAL_COPY]: new AnalyticEvent('referral-bonus-modal-copy'),
    [AnalyticEventNames.REFERRAL_BONUS_MODAL_CLOSE]: new AnalyticEvent('referral-bonus-modal-close'),
    [AnalyticEventNames.REFERRAL_BONUS_INFO_SHOW]: new AnalyticEvent('referral-bonus-info-show'),
    [AnalyticEventNames.REFERRAL_BONUS_INFO_CLOSE]: new AnalyticEvent('referral-bonus-info-close'),
    [AnalyticEventNames.REFERRAL_FAILED_MODAL_SHOW]: new AnalyticEvent('referral-failed-modal-show'),
    [AnalyticEventNames.REFERRAL_FAILED_MODAL_CLICK]: new AnalyticEvent('referral-failed-modal-click'),
    [AnalyticEventNames.REFERRAL_FAILED_MODAL_CLOSE]: new AnalyticEvent('referral-failed-modal-close'),
    // tempexp_18086-end

    // tempexp_18926-start
    [AnalyticEventNames.AUTOUPLOAD_ONBOARDING_SHOW]: new AnalyticEvent('autoupload-onboarding-show'),
    [AnalyticEventNames.AUTOUPLOAD_ONBOARDING_CLICK]: new AnalyticEvent('autoupload-onboarding-click'),
    [AnalyticEventNames.AUTOUPLOAD_ONBOARDING_FINAL_SHOW]: new AnalyticEvent('autoupload-onboarding-final-show'),
    [AnalyticEventNames.AUTOUPLOAD_ONBOARDING_FINAL_CLICK]: new AnalyticEvent('autoupload-onboarding-final-click'),
    [AnalyticEventNames.AUTOUPLOAD_CANCEL_SHOW]: new AnalyticEvent('autoupload-cancel-show'),
    [AnalyticEventNames.AUTOUPLOAD_CANCEL_SAVE]: new AnalyticEvent('autoupload-cancel-save'),
    [AnalyticEventNames.AUTOUPLOAD_CANCEL]: new AnalyticEvent('autoupload-cancel'),
    // tempexp_18926-end

    // tempexp_19300-start
    [AnalyticEventNames.DISCOUNT_ON_REMOVE_MODAL_SHOW]: new AnalyticEvent('discount-remove-modal-show'),
    [AnalyticEventNames.DISCOUNT_ON_REMOVE_MODAL_CLICK]: new AnalyticEvent('discount-remove-modal-click'),
    [AnalyticEventNames.DISCOUNT_ON_REMOVE_MODAL_CANCEL]: new AnalyticEvent('discount-remove-modal-cancel'),
    [AnalyticEventNames.DISCOUNT_ON_REMOVE_MODAL_CLOSE]: new AnalyticEvent('discount-remove-modal-close'),
    // tempexp_19300-end

    [AnalyticEventNames.VK_MUSIC_OFFER_SHOW]: new AnalyticEvent<VkMusicOfferArgs>('vkmusic-offer-show'),
    [AnalyticEventNames.VK_MUSIC_OFFER_CLICK]: new AnalyticEvent<VkMusicOfferArgs>('vkmusic-offer-click'),
    [AnalyticEventNames.VK_MUSIC_OFFER_CLOSE]: new AnalyticEvent<VkMusicOfferArgs>('vkmusic-offer-close'),

    [AnalyticEventNames.VK_MUSIC_CONFIRM_SHOW]: new AnalyticEvent<VkMusicConfirmArgs>('vkmusic-confirm-show'),
    [AnalyticEventNames.VK_MUSIC_CONFIRM_CONTINUE]: new AnalyticEvent<VkMusicConfirmArgs>('vkmusic-confirm-continue'),
    [AnalyticEventNames.VK_MUSIC_CONFIRM_CHOOSE]: new AnalyticEvent<VkMusicConfirmArgs>('vkmusic-confirm-choose'),
    [AnalyticEventNames.VK_MUSIC_CONFIRM_CLOSE]: new AnalyticEvent<VkMusicConfirmArgs>('vkmusic-confirm-close'),

    [AnalyticEventNames.VK_MUSIC_ERROR_SHOW]: new AnalyticEvent<VkMusicErrorArgs>('vkmusic-error-show'),
    [AnalyticEventNames.VK_MUSIC_ERROR_BUY]: new AnalyticEvent<VkMusicErrorArgs>('vkmusic-error-buy'),
    [AnalyticEventNames.VK_MUSIC_ERROR_CHOOSE]: new AnalyticEvent<VkMusicErrorArgs>('vkmusic-error-choose'),
    [AnalyticEventNames.VK_MUSIC_ERROR_CLOSE]: new AnalyticEvent<VkMusicErrorArgs>('vkmusic-error-close'),

    [AnalyticEventNames.VK_MUSIC_SUCCESS_SHOW]: new AnalyticEvent<VkMusicSuccessArgs>('vkmusic-success-show'),
    [AnalyticEventNames.VK_MUSIC_SUCCESS_CLICK]: new AnalyticEvent<VkMusicSuccessClickArgs>('vkmusic-success-click'),
    [AnalyticEventNames.VK_MUSIC_SUCCESS_CLOSE]: new AnalyticEvent<VkMusicSuccessArgs>('vkmusic-success-close'),

    [AnalyticEventNames.VK_MUSIC_OFFER_LANDING_VIEW]: new AnalyticEvent('vkmusic-banner-landing-view'),
    [AnalyticEventNames.VK_MUSIC_OFFER_LANDING_CLICK]: new AnalyticEvent('vkmusic-banner-landing-click'),

    // tempexp_19232-start
    [AnalyticEventNames.MAY_PROMO_2025_BUY_TARIFF_SHOW]: new AnalyticEvent('may-promo-2025-btn-show'),
    [AnalyticEventNames.MAY_PROMO_2025_BUY_TARIFF_CLICK]: new AnalyticEvent('may-promo-2025-btn-click'),
    // tempexp_19232-end

    // tempexp_19370-start
    /** показ попапа о приоритетном скачивании */
    [AnalyticEventNames.PRIORITY_DOWNLOAD_SHOW]: new AnalyticEvent('priority-download-show'),
    [AnalyticEventNames.PRIORITY_DOWNLOAD_CLICK]: new AnalyticEvent('priority-download-click'),
    // tempexp_19370-end

    // tempexp_19094-start
    [AnalyticEventNames.FULLSCREEN_SHOW]: new AnalyticEvent('fullscreen-show'),
    [AnalyticEventNames.FULLSCREEN_CLICK]: new AnalyticEvent('fullscreen-click'),
    [AnalyticEventNames.FULLSCREEN_CLICK_ALL]: new AnalyticEvent('fullscreen-click-all'),
    [AnalyticEventNames.FULLSCREEN_CLOSE]: new AnalyticEvent('fullscreen-close'),
    // tempexp_19094-end

    [AnalyticEventNames.BILLING_BUY_TARIFF_SUCCESS]: new AnalyticEvent<BillingBuyTariffArgs>('billing-buy-tariff-success'),
    [AnalyticEventNames.BILLING_BUY_TARIFF_ERROR]: new AnalyticEvent<BillingBuyTariffArgs>('billing-buy-tariff-error'),

    [AnalyticEventNames.MULTITARIFF_TYPE_CLICK_M]: new AnalyticEvent('multitariff-type-click-m'),
    [AnalyticEventNames.MULTITARIFF_SIZE_CLICK_M]: new AnalyticEvent('multitariff-size-click-m'),

    // tempexp_19373-start
    [AnalyticEventNames.BEAUTIFUL_ADDRESS_MODAL_SHOW]: new AnalyticEvent('address-show'),
    [AnalyticEventNames.BEAUTIFUL_ADDRESS_MODAL_CLICK]: new AnalyticEvent('address-click'),
    [AnalyticEventNames.BEAUTIFUL_ADDRESS_MODAL_CLOSE]: new AnalyticEvent('address-close'),
    // tempexp_19373-end

    // tempexp_19420-start
    [AnalyticEventNames.EDITOR_TOOLS_RENAME_CLICK]: new AnalyticEvent('editor-tools-rename-click'),
    [AnalyticEventNames.EDITOR_TOOLS_SEND_ATTACH_CLICK]: new AnalyticEvent('editor-tools-send-attach-click'),
    // tempexp_19420-end

    // tempexp_19513-start
    [AnalyticEventNames.UPSALE_MULTI_MODAL_SHOW]: new AnalyticEvent('upsale-multi-modal-show'),
    [AnalyticEventNames.UPSALE_MULTI_MODAL_SWITCH]: new AnalyticEvent('upsale-multi-modal-switch'),
    [AnalyticEventNames.UPSALE_MULTI_MODAL__CONTINUE]: new AnalyticEvent('upsale-multi-modal-continue'),
    [AnalyticEventNames.UPSALE_MULTI_MODAL_CLOSE]: new AnalyticEvent('upsale-multi-modal-close'),
    // tempexp_19513-end

    // tempexp_19617-start
    [AnalyticEventNames.UNLIMITED_DOCUMENTS_MODAL_SHOW]: new AnalyticEvent<UnlimitedDocumentsModalArgs>('unlimited-documents-modal-show'),
    [AnalyticEventNames.UNLIMITED_DOCUMENTS_MODAL_CLICK]: new AnalyticEvent<UnlimitedDocumentsModalArgs>('unlimited-documents-modal-click'),
    [AnalyticEventNames.UNLIMITED_DOCUMENTS_MODAL_CLOSE]: new AnalyticEvent<UnlimitedDocumentsModalArgs>('unlimited-documents-modal-close'),

    [AnalyticEventNames.UNLIMITED_DOCUMENTS_THANKS_MODAL_SHOW]: new AnalyticEvent<UnlimitedDocumentsEntryPointArg>(
        'unlimited-documents-thanks-modal-show'
    ),
    [AnalyticEventNames.UNLIMITED_DOCUMENTS_THANKS_MODAL_CLICK]: new AnalyticEvent<UnlimitedDocumentsEntryPointArg>(
        'unlimited-documents-thanks-modal-click'
    ),
    [AnalyticEventNames.UNLIMITED_DOCUMENTS_THANKS_MODAL_CLOSE]: new AnalyticEvent<UnlimitedDocumentsEntryPointArg>(
        'unlimited-documents-thanks-modal-close'
    ),

    [AnalyticEventNames.UNLIMITED_DOCUMENTS_ASIDE_BANNER_SHOW]: new AnalyticEvent<UnlimitedDocumentsDocTypeArg>(
        'unlimited-documents-aside-banner-show'
    ),
    [AnalyticEventNames.UNLIMITED_DOCUMENTS_ASIDE_BANNER_CLICK]: new AnalyticEvent<UnlimitedDocumentsDocTypeArg>(
        'unlimited-documents-aside-banner-click'
    ),
    [AnalyticEventNames.UNLIMITED_DOCUMENTS_ASIDE_BANNER_CLOSE]: new AnalyticEvent<UnlimitedDocumentsDocTypeArg>(
        'unlimited-documents-aside-banner-close'
    ),

    [AnalyticEventNames.UNLIMITED_DOCUMENTS_SIDEBAR_BANNER_SHOW]: new AnalyticEvent<UnlimitedDocumentsDocTypeArg>(
        'unlimited-documents-sidebar-banner-show'
    ),
    [AnalyticEventNames.UNLIMITED_DOCUMENTS_SIDEBAR_BANNER_CLICK]: new AnalyticEvent<UnlimitedDocumentsDocTypeArg>(
        'unlimited-documents-sidebar-banner-click'
    ),
    // tempexp_19617-end
});
